import { useMemo, useState } from 'react'
import {
  HOUSE_SET,
  REFERENCE_COUNT,
  jobFromBuild,
  resolveReference,
  rungNote,
  selectPrecedent,
  verificationNote,
} from '../references.js'
import styles from './PrecedentPanel.module.css'

/**
 * THE PRECEDENT PANEL — which prior job this build may lean on, and exactly
 * where the line is.
 *
 * SR's working method is to open a nearby job as a starting point; that is why
 * SR reaches a finished piece fast. The risk in it is one specific thing, and
 * it is not the file — it is the MARK. So the panel is built around the split
 * that makes both true at once:
 *
 *   construction · house look · technique  → open across accounts. SR's craft.
 *   client identity                        → never across accounts.
 *
 * Both columns are on screen together, because a rule the operator has to
 * remember is a rule that gets broken on a Friday. "May take" without "must not
 * take" beside it is the version that hands out a licensed interlock.
 *
 * IT PROPOSES. The pick is scored and shown WITH ITS REASON and with the
 * alternates one click away, so a rep can disagree with it. Nothing here writes
 * to the build state and nothing here reaches the render payload.
 */
export default function PrecedentPanel({ build, championship, school }) {
  const job = useMemo(() => jobFromBuild(build, { championship, school }), [build, championship, school])
  const pick = useMemo(() => selectPrecedent(job), [job])

  const [chosenId, setChosenId] = useState(null)
  const [showAlts, setShowAlts] = useState(false)

  // The operator's override wins over the score; clearing it reverts to the pick.
  const active = useMemo(() => {
    if (!pick.ok) return null
    const id = chosenId || pick.selected
    return pick.candidates.find((c) => c.reference_id === id) || pick.candidates[0] || null
  }, [pick, chosenId])

  const address = useMemo(
    () => (active ? resolveReference(active.reference_id) : null),
    [active]
  )

  return (
    <section className={`card ${styles.card}`}>
      <div className="field-label">Precedent — what this build may lean on</div>

      {!pick.ok ? (
        <>
          <p className={styles.rung}>{rungNote(pick.rung)}</p>
          <p className={styles.note}>
            The {HOUSE_SET.length} house-look references still apply. The index holds {REFERENCE_COUNT} references
            today; breadth is what the selector runs on, so a sport with no entry says so rather than
            offering a precedent it did not find.
          </p>
        </>
      ) : (
        <>
          <p className={styles.rung}>{rungNote(pick.rung)}</p>

          <div className={styles.pick}>
            <strong className={styles.pickTitle}>{active.title}</strong>
            <span className={styles.pickMeta}>
              {active.reference_id}
              {active.customer ? ` · ${active.customer}` : ''}
            </span>
            {!!active.why.length && <span className={styles.why}>Matched on: {active.why.join(', ')}</span>}
          </div>

          {/* THE SPLIT. Both halves, always, side by side. */}
          <div className="field-label">May take — construction, house look, technique</div>
          <ul className={styles.take}>
            {active.mayTake.length
              ? active.mayTake.map((a) => <li key={a} className={styles.may}>{a}</li>)
              : <li className={styles.empty}>Nothing recorded on this reference yet.</li>}
          </ul>

          <div className="field-label">Must not take — this account&apos;s identity</div>
          <ul className={styles.take}>
            {active.mustNotTake.length
              ? active.mustNotTake.map((a) => <li key={a} className={styles.mustNot}>{a}</li>)
              : <li className={styles.empty}>Nothing on this reference is fenced for this job.</li>}
          </ul>
          <p className={styles.identity}>{pick.identityNote}</p>

          {active.avoidPatterns && <p className={styles.avoid}>{active.avoidPatterns}</p>}

          {/* THREE verification states, three sentences. "Never checked" is not
              "very stale", and neither is silence. */}
          <p className={styles[verifyClass(active.verification)]}>
            {verificationNote(active.verification)}
          </p>

          {/* A URL is a fetch, never an address — so the panel shows the CALL,
              not a link that will have expired by the time anybody clicks it. */}
          {address && address.ok && (
            <p className={styles.note}>
              Fetch via <code>{address.fetchPlan.call}</code> ({address.fetchPlan.via}) —{' '}
              {address.fetchPlan.urlLifetime}.
            </p>
          )}
          {address && !address.ok && (
            <p className={styles.avoid}>
              No durable address on this reference ({address.error}) — the file cannot be fetched from the
              registry alone. The construction notes above still stand.
            </p>
          )}

          {pick.candidates.length > 1 && (
            <>
              <button type="button" className={styles.altBtn} onClick={() => setShowAlts((v) => !v)}>
                {showAlts ? 'Hide alternatives' : `Alternatives (${pick.candidates.length - 1})`}
              </button>
              {showAlts && (
                <ul className={styles.alts}>
                  {pick.candidates.map((c) => (
                    <li key={c.reference_id}>
                      <button
                        type="button"
                        className={`${styles.alt} ${c.reference_id === active.reference_id ? styles.altOn : ''}`}
                        onClick={() => setChosenId(c.reference_id)}
                      >
                        <span className={styles.altTitle}>{c.title}</span>
                        <span className={styles.altWhy}>{c.why.join(', ') || 'no matching attribute'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}
    </section>
  )
}

function verifyClass(state) {
  if (state === 'FRESH') return 'verifyOk'
  if (state === 'STALE') return 'verifyStale'
  return 'verifyNever'
}
